import React, { PureComponent, Suspense } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import * as actions from '../../actions/AppActions';
import NavHeader from './NavHeader/NavHeader';
import NavComponents from './NavComponents/NavComponents';
import Search from '../Search/Search';
import { NavigationContext } from './Context';
import { ThemeContext } from '../App/ThemeContext';
import { setHash } from '../../utils/help';
import classes from './Navigation.module.scss';

const Instructions = React.lazy(() => import('../Instructions/Instructions'));
const Bookmarks = React.lazy(() => import('../Bookmarks/Bookmarks'));
const Notes = React.lazy(() => import('../Notes/Notes'));
const Favorites = React.lazy(() => import('../Favorites/Favorites'));
const ScreenCapture = React.lazy(() => import('../ScreenCapture/ScreenCapture'));

function mapStateToProps(state) {
  return {
    selected: state.main.selected,
    modules: state.main.modules,
    contents: state.main.contents,
    filterContent: state.main.filterContent,
    fillModules: state.main.fillModules,
    setTopic: state.main.setTopic,
    setAct: state.main.setAct,
    setContent: state.main.setContent,
    fillSelected: state.main.fillSelected,
    finishModule: state.main.finishModule,
    commonSettings: state.main.commonSettings,
    openContent: state.main.openContent,
    setSelectedContent: state.main.setSelectedContent,
    startPage: state.startPage.startPage,
  };
}

function withToast(Component) {
  return function WrappedComponent(props) {
    const toastFuncs = useToasts();
    return <Component {...props} {...toastFuncs} />;
  };
}

const getCurIndex = (modules, searchId) => modules.findIndex((elem) => (elem.show && elem.id.toString() === searchId.toString()));

class Navigation extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isVisible: true,
      filter: '',
    };

    this.onVisibleChange = this.onVisibleChange.bind(this);
    this.updateSearch = this.updateSearch.bind(this);
    this.prevClick = this.prevClick.bind(this);
    this.nextClick = this.nextClick.bind(this);
  }

  componentDidMount() {
    window.addEventListener('hashchange', () => {
      this.changeModule(window.location.hash.substring(1));
    }, false);

    const {
      selected,
      commonSettings,
    } = this.props;

    this.changeModule(selected.content);

    if (commonSettings.saveStats === 'true') {
      window.addEventListener('unload', () => {
        this.forceClose();
      });
    }
  }

  onVisibleChange(flag) {
    this.setState({
      isVisible: flag,
    });

    const { navBarVisibleChange } = this.props;
    navBarVisibleChange(flag);
  }

  forceClose() {
    const {
      selected,
      commonSettings,
    } = this.props;

    window.result.forceLeaveModule(selected.content, commonSettings.courseId);
  }

  changeTopic(index) {
    const { selected, setTopic } = this.props;

    if (selected.topic === index) setTopic(-1);
    else setTopic(index);
  }

  changeAct(index) {
    const { selected, setAct } = this.props;

    if (selected.act === index) setAct(-1);
    else setAct(index);
  }

  nextClick() {
    const { selected, modules } = this.props;
    const curIndex = getCurIndex(modules, selected.content);
    setHash(modules[curIndex + 1].id);
  }

  prevClick() {
    const { selected, modules } = this.props;
    const curIndex = getCurIndex(modules, selected.content);
    setHash(modules[curIndex - 1].id);
  }

  async changeModule(moduleId) {
    const {
      contents,
      modules,
      selected,
      fillModules,
      fillSelected,
      openContent,
      finishModule,
      commonSettings,
      setSelectedContent,
    } = this.props;

    if (commonSettings.saveStats === 'true') {
      window.navigation.activeModule = moduleId;

      // отправляем закрытие предыдущего модуля
      if (moduleId !== selected.content) {
        window.result.leaveModule(
          selected.content,
          commonSettings.courseId,
          this.SuccessSaveResults.bind(this),
          this.FailSaveResults.bind(this),
        );
      }

      // отправляем открытие текущего модуля
      window.result.openModule(moduleId, commonSettings.courseId);
    }

    setSelectedContent(moduleId).then(() => {
      fillSelected(contents, moduleId);
    });

    const promise = new Promise((resolve) => {
      if (commonSettings.completeIcon === 'true') {
        if (moduleId !== selected.content) {
          if (selected.behavior !== 'check' && selected.behavior !== 'test' && selected.behavior !== 'practice') {
            if (modules.find((module) => module.id.toString() === selected.content.toString()).finished === 'false') {
              finishModule(contents, selected.content).then((content) => {
                fillModules(content);
                resolve(content);
              });
            } else resolve(contents);
          } else resolve(contents);
        } else resolve(contents);
      } else resolve(contents);
    });
    const resultContent = await promise;

    if (commonSettings.order === 'true') {
      if (modules.find((module) => module.id.toString() === moduleId.toString()).opened === 'false') {
        openContent(resultContent, moduleId).then((content) => {
          fillModules(content);
        });
      }
    }
  }

  SuccessSaveResults(response) {
    if (response.hasOwnProperty('errors') && response.errors.toString() === 'true') this.FailSaveResults(null);
  }

  FailSaveResults(error) {
    const { t, addToast } = this.props;
    addToast(
      `${t('toast.failSaveResults')} (leave)`,
      { autoDismiss: true, appearance: 'error', placement: 'top-left' },
    );
  }

  updateSearch(value) {
    const { contents, filterContent, fillModules } = this.props;

    this.setState({
      filter: value.toLowerCase(),
    });

    filterContent(contents, value.toLowerCase()).then((filteredContent) => {
      fillModules(filteredContent);
    });
  }

  render() {
    const {
      commonSettings,
      iframe,
      modules,
      contents,
      selected,
      startPage,
    } = this.props;
    const {
      isVisible,
      filter,
    } = this.state;

    const { theme } = this.context;
    const instruments = [];

    if (commonSettings.comments === 'true') {
      instruments.push(
        <Suspense key="comments" fallback={null}>
          <Bookmarks
            iframe={iframe}
          />
          <Notes
            iframe={iframe}
          />
        </Suspense>,
      );
    }
    if (commonSettings.favorites === 'true') {
      instruments.push(
        <Suspense key="favorites" fallback={null}>
          <Favorites />
        </Suspense>,
      );
    }
    if (commonSettings.screenCapture === 'true') {
      instruments.push(
        <Suspense key="screen_capture" fallback={null}>
          <ScreenCapture />
        </Suspense>,
      );
    }

    // скрываем навбар, если у нас режим стартовой страницы или принудительно скрыт
    if (Object.keys(startPage).length > 0 ||
        commonSettings.hideNavbar === 'true' ) return null;

    return (
      <div
        id="navbar"
        className={`${classes.Navbar} ${isVisible ? '' : classes.closed} ${theme ? classes[theme] : ''}`}
      >
        <Suspense fallback={null}>
          <NavHeader
            isVisible={isVisible}
            onVisibleChange={this.onVisibleChange}
            onPrevClick={this.prevClick}
            onNextClick={this.nextClick}
            curContent={selected.content}
            modules={modules}
            commonSettings={commonSettings}
          />
          <NavigationContext.Provider
            value={
              {
                contents,
                modules,
                selected,
                filter,
                completeIcon: commonSettings.completeIcon,
                order: commonSettings.order,
                onTopicClick: this.changeTopic.bind(this),
                onActClick: this.changeAct.bind(this),
              }
            }
          >
            <NavComponents />
          </NavigationContext.Provider>
          {commonSettings.instructions === 'true' && (
            <Suspense fallback={null}>
              <Instructions />
            </Suspense>
          )}
          {(commonSettings.comments === 'true' || commonSettings.favorites === 'true' || commonSettings.screenCapture === 'true') && (
            <div className={classes.Instruments}>
              {instruments}
            </div>
          )}
          <Search filter={filter} onSearchInput={this.updateSearch} />
        </Suspense>
      </div>
    );
  }
}

Navigation.contextType = ThemeContext;

export default withTranslation()(
               withToast(
                 connect(mapStateToProps,actions)(Navigation)
               ));

Navigation.propTypes = {
  addToast: PropTypes.func.isRequired,
  navBarVisibleChange: PropTypes.func.isRequired,
  iframe: PropTypes.instanceOf(Element),
  selected: PropTypes.objectOf(PropTypes.any).isRequired,
  modules: PropTypes.arrayOf(PropTypes.object).isRequired,
  contents: PropTypes.arrayOf(PropTypes.object).isRequired,
  startPage: PropTypes.objectOf(PropTypes.any).isRequired,
  filterContent: PropTypes.func.isRequired,
  fillModules: PropTypes.func.isRequired,
  setTopic: PropTypes.func.isRequired,
  setAct: PropTypes.func.isRequired,
  setSelectedContent: PropTypes.func.isRequired,
  openContent: PropTypes.func.isRequired,
  fillSelected: PropTypes.func.isRequired,
  finishModule: PropTypes.func.isRequired,
  commonSettings: PropTypes.shape({
    base: PropTypes.string.isRequired,
    courseId: PropTypes.string.isRequired,
    lang: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    background: PropTypes.string.isRequired,
    theme: PropTypes.string.isRequired,
    saveStats: PropTypes.string,
    order: PropTypes.string,
    completeIcon: PropTypes.string,
    instructions: PropTypes.string,
    comments: PropTypes.string,
    favorites: PropTypes.string,
    screenCapture: PropTypes.string,
    hideNavbar: PropTypes.string,
  }).isRequired,
};

Navigation.defaultProps = {
  iframe: null,
};
